import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ChevronRight, ChevronLeft, Menu, X, ExternalLink, HelpCircle, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { SiX } from 'react-icons/si';

// AIBL Logo
const LOGO = 'https://customer-assets.emergentagent.com/job_3c7ea79e-55a3-44e4-9827-1c3ee83e6f4f/artifacts/pc4k8dbt_Group%2083.png';

// Documentation structure
export const docsStructure = [
  {
    section: 'Getting Started',
    items: [
      { title: 'Introduction', path: '/docs', description: 'Overview of AIBL platform' },
      { title: 'Quick Start', path: '/docs/quickstart', description: 'Get up and running in minutes' },
      { title: 'Architecture', path: '/docs/architecture', description: 'How AIBL works under the hood' },
    ]
  },
  {
    section: 'Core Concepts',
    items: [
      { title: 'Agents', path: '/docs/agents', description: 'Understanding AI agents' },
      { title: 'Plugins', path: '/docs/plugins', description: 'Extend agent capabilities' },
      { title: 'Wallets', path: '/docs/wallets', description: 'Multi-chain wallet management' },
      { title: 'Multi-Sig', path: '/docs/multisig', description: 'Gnosis Safe integration' },
    ]
  },
  {
    section: 'Protocol',
    items: [
      { title: 'Tokenomics', path: '/docs/tokenomics', description: 'Token economics and distribution' },
      { title: 'Governance', path: '/docs/governance', description: 'DAO and voting mechanisms' },
      { title: 'Fees', path: '/docs/fees', description: 'Fee structure and revenue sharing' },
    ]
  },
  {
    section: 'API Reference',
    items: [
      { title: 'Authentication', path: '/docs/api/auth', description: 'API authentication methods' },
      { title: 'Endpoints', path: '/docs/api/endpoints', description: 'Available API endpoints' },
      { title: 'Webhooks', path: '/docs/api/webhooks', description: 'Real-time event notifications' },
    ]
  },
];

// Flatten docs for navigation
const flatDocs = docsStructure.flatMap(section => section.items);

// Search function
const searchDocs = (query) => {
  if (!query.trim()) return [];
  const lowerQuery = query.toLowerCase();
  return flatDocs.filter(doc => 
    doc.title.toLowerCase().includes(lowerQuery) ||
    doc.description.toLowerCase().includes(lowerQuery)
  );
};

// Get current doc index for prev/next navigation
const getCurrentDocIndex = (pathname) => {
  return flatDocs.findIndex(doc => doc.path === pathname);
};

// Right panel content configurations for each page
const getRightPanelContent = (pathname, currentDoc) => {
  const panels = {
    '/docs': {
      type: 'welcome',
      title: 'Welcome to AIBL',
      icon: '🚀',
      gradient: 'from-blue-500/20 via-purple-500/10 to-transparent',
      stats: [
        { label: 'Active Agents', value: '2,400+' },
        { label: 'Chains', value: '20+' },
        { label: 'Plugins', value: '12' },
      ],
      tip: 'Press "/" to search docs quickly',
    },
    '/docs/quickstart': {
      type: 'steps',
      title: 'Quick Setup',
      icon: '⚡',
      gradient: 'from-green-500/20 via-emerald-500/10 to-transparent',
      steps: ['Create Account', 'Deploy Agent', 'Configure', 'Go Live'],
      eta: '~5 min setup',
    },
    '/docs/architecture': {
      type: 'diagram',
      title: 'System Layers',
      icon: '🏗️',
      gradient: 'from-orange-500/20 via-amber-500/10 to-transparent',
      layers: ['Dashboard', 'API Layer', 'Execution', 'Settlement'],
    },
    '/docs/agents': {
      type: 'levels',
      title: 'Autonomy Scale',
      icon: '🤖',
      gradient: 'from-cyan-500/20 via-blue-500/10 to-transparent',
      levels: [
        { level: 'L0', name: 'Manual', color: 'green' },
        { level: 'L1', name: 'Reactive', color: 'blue' },
        { level: 'L2', name: 'Proactive', color: 'purple' },
        { level: 'L3', name: 'Autonomous', color: 'orange' },
      ],
    },
    '/docs/plugins': {
      type: 'plugins',
      title: 'Available Plugins',
      icon: '🧩',
      gradient: 'from-violet-500/20 via-purple-500/10 to-transparent',
      plugins: ['Swap', 'Transfer', 'Bridge', 'Wrap', 'Buy'],
    },
    '/docs/wallets': {
      type: 'chains',
      title: 'Supported Chains',
      icon: '⛓️',
      gradient: 'from-teal-500/20 via-cyan-500/10 to-transparent',
      chains: ['ETH', 'RBH', 'ARB', 'POLY', 'OP', 'AVAX'],
    },
    '/docs/multisig': {
      type: 'security',
      title: 'Multi-Sig Security',
      icon: '🔐',
      gradient: 'from-red-500/20 via-pink-500/10 to-transparent',
      features: ['M-of-N Signing', 'Gnosis Safe', 'Co-signer URLs'],
    },
    '/docs/tokenomics': {
      type: 'distribution',
      title: 'Token Split',
      icon: '🪙',
      gradient: 'from-yellow-500/20 via-amber-500/10 to-transparent',
      distribution: [
        { name: 'Community', pct: 40, color: 'green' },
        { name: 'Team', pct: 20, color: 'blue' },
        { name: 'Investors', pct: 15, color: 'purple' },
        { name: 'Treasury', pct: 15, color: 'yellow' },
        { name: 'Liquidity', pct: 10, color: 'orange' },
      ],
    },
    '/docs/governance': {
      type: 'governance',
      title: 'DAO Proposals',
      icon: '🗳️',
      gradient: 'from-indigo-500/20 via-blue-500/10 to-transparent',
      types: ['AIP', 'AGP', 'ARP'],
    },
    '/docs/fees': {
      type: 'fees',
      title: 'Fee Tiers',
      icon: '💰',
      gradient: 'from-emerald-500/20 via-green-500/10 to-transparent',
      tiers: [
        { name: 'Bronze', discount: '5%' },
        { name: 'Silver', discount: '15%' },
        { name: 'Gold', discount: '25%' },
        { name: 'Platinum', discount: '50%' },
      ],
    },
    '/docs/api/auth': {
      type: 'api',
      title: 'Auth Methods',
      icon: '🔑',
      gradient: 'from-rose-500/20 via-red-500/10 to-transparent',
      methods: ['API Keys', 'JWT Tokens', 'Wallet Signing'],
    },
    '/docs/api/endpoints': {
      type: 'api',
      title: 'API Groups',
      icon: '📡',
      gradient: 'from-sky-500/20 via-blue-500/10 to-transparent',
      methods: ['Agents', 'Plugins', 'Wallets', 'Safe'],
    },
    '/docs/api/webhooks': {
      type: 'events',
      title: 'Event Types',
      icon: '🔔',
      gradient: 'from-fuchsia-500/20 via-pink-500/10 to-transparent',
      events: ['tx.pending', 'tx.confirmed', 'tx.failed', 'low_balance'],
    },
  };

  return panels[pathname] || {
    type: 'default',
    title: currentDoc?.title || 'Documentation',
    icon: '📖',
    gradient: 'from-white/10 via-white/5 to-transparent',
  };
};

export default function DocsLayout({ children, title, description, rightPanel }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const currentIndex = getCurrentDocIndex(location.pathname);
  const prevDoc = currentIndex > 0 ? flatDocs[currentIndex - 1] : null;
  const nextDoc = currentIndex < flatDocs.length - 1 ? flatDocs[currentIndex + 1] : null;
  
  // Get default right panel content based on current page
  const currentDoc = flatDocs[currentIndex];
  const defaultRightPanel = getRightPanelContent(location.pathname, currentDoc);

  // Handle search
  useEffect(() => {
    const results = searchDocs(searchQuery);
    setSearchResults(results);
  }, [searchQuery]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === '/' && !isSearchFocused) {
        e.preventDefault();
        document.getElementById('docs-search')?.focus();
      }
      if (e.key === 'Escape') {
        setSearchQuery('');
        setIsSearchFocused(false);
        document.getElementById('docs-search')?.blur();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchFocused]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Top Navigation Bar */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/5">
        <div className="h-full flex items-center justify-between px-4 lg:px-6">
          {/* Left: Logo + Mobile Menu Button */}
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 hover:bg-white/5 rounded-lg transition-colors"
              aria-label="Toggle menu"
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO} alt="AIBL" className="h-6" />
              <span className="text-white/40 text-sm font-medium hidden sm:inline">Docs</span>
            </Link>
          </div>

          {/* Center: Search Bar */}
          <div className="flex-1 max-w-xl mx-4 lg:mx-8 relative">
            <div className={`relative transition-all duration-200 ${isSearchFocused ? 'ring-2 ring-white/20' : ''} rounded-xl`}>
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                id="docs-search"
                type="text"
                placeholder="Search docs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                className="w-full bg-white/5 border border-white/10 rounded-xl py-2.5 pl-11 pr-12 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors"
                data-testid="docs-search-input"
              />
              <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-white/30 bg-white/5 rounded border border-white/10">
                /
              </kbd>
            </div>

            {/* Search Results Dropdown */}
            <AnimatePresence>
              {isSearchFocused && searchQuery.trim() && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 right-0 mt-2 bg-[#141414] border border-white/10 rounded-xl shadow-2xl overflow-hidden"
                  data-testid="search-results-dropdown"
                >
                  {searchResults.length > 0 ? (
                    searchResults.map((result, i) => (
                      <Link
                        key={result.path}
                        to={result.path}
                        onClick={() => setSearchQuery('')}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                        data-testid={`search-result-${i}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                          <ChevronRight size={14} className="text-white/40" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">{result.title}</p>
                          <p className="text-xs text-white/40">{result.description}</p>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="px-4 py-6 text-center">
                      <p className="text-sm text-white/40">No results found for "{searchQuery}"</p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Right: Social Links */}
          <div className="flex items-center gap-3">
            <Link 
              to="/docs" 
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-sm text-white/60 hover:text-white transition-colors"
            >
              Docs
            </Link>
            <a 
              href="https://x.com/aibl" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-2 hover:bg-white/5 rounded-lg transition-colors text-white/60 hover:text-white"
              aria-label="X (Twitter)"
              data-testid="social-x-link"
            >
              <SiX size={16} />
            </a>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/60 z-40 lg:hidden"
            />
            <motion.aside
              initial={{ x: -260 }}
              animate={{ x: 0 }}
              exit={{ x: -260 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed left-0 top-16 bottom-0 w-[260px] bg-[#0a0a0a] border-r border-white/5 z-40 lg:hidden overflow-y-auto scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <Sidebar currentPath={location.pathname} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Layout */}
      <div className="flex pt-16">
        {/* Desktop Sidebar - Fixed, scrollable but hidden scrollbar */}
        <aside className="hidden lg:block fixed left-0 top-16 bottom-0 w-[260px] border-r border-white/5 bg-[#0a0a0a] overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <Sidebar currentPath={location.pathname} />
        </aside>

        {/* Main Content - Scrollable */}
        <main className="flex-1 lg:ml-[260px] xl:mr-[280px] min-h-[calc(100vh-64px)]">
          <div className="max-w-3xl px-6 lg:px-10 py-12">
            {/* Page Header */}
            {title && (
              <div className="mb-10">
                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-3">{title}</h1>
                {description && (
                  <p className="text-lg text-white/50">{description}</p>
                )}
              </div>
            )}

            {/* Page Content */}
            <div className="prose prose-invert prose-lg max-w-none">
              {children}
            </div>

            {/* Prev/Next Navigation */}
            <nav className="flex flex-col sm:flex-row items-stretch gap-4 mt-16 pt-8 border-t border-white/10" data-testid="docs-nav">
              {prevDoc ? (
                <Link
                  to={prevDoc.path}
                  className="flex-1 group"
                  data-testid="prev-doc-link"
                >
                  <DocNavCard direction="prev" title={prevDoc.title} />
                </Link>
              ) : <div className="flex-1" />}
              
              {nextDoc ? (
                <Link
                  to={nextDoc.path}
                  className="flex-1 group"
                  data-testid="next-doc-link"
                >
                  <DocNavCard direction="next" title={nextDoc.title} />
                </Link>
              ) : <div className="flex-1" />}
            </nav>
          </div>
        </main>

        {/* Right Panel - Desktop only (xl breakpoint to avoid cramped content at 1024px) */}
        <aside className="hidden xl:block fixed right-0 top-16 bottom-0 w-[280px] p-6 overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }} data-testid="docs-right-panel-container">
          <RightPanel content={rightPanel || defaultRightPanel} />
        </aside>
      </div>
    </div>
  );
}

// Sidebar Component
function Sidebar({ currentPath }) {
  return (
    <nav className="py-6 px-4">
      {docsStructure.map((section, sectionIndex) => (
        <div key={section.section} className={sectionIndex > 0 ? 'mt-8' : ''}>
          <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-3 px-3">
            {section.section}
          </h3>
          <ul className="space-y-1">
            {section.items.map((item) => {
              const isActive = currentPath === item.path;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`block px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                      isActive 
                        ? 'bg-white/10 text-white font-medium' 
                        : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {item.title}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

// Right Panel Component - Custom content per page
function RightPanel({ content }) {
  const { type, title, icon, gradient, stats, tip, steps, eta, layers, levels, plugins, chains, features, distribution, types, tiers, methods, events } = content;

  // Static color map for Tailwind classes
  const colorMap = {
    green: { bg: 'bg-green-500/20', text: 'text-green-400', bar: 'bg-green-500' },
    blue: { bg: 'bg-blue-500/20', text: 'text-blue-400', bar: 'bg-blue-500' },
    purple: { bg: 'bg-purple-500/20', text: 'text-purple-400', bar: 'bg-purple-500' },
    orange: { bg: 'bg-orange-500/20', text: 'text-orange-400', bar: 'bg-orange-500' },
    yellow: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', bar: 'bg-yellow-500' },
    red: { bg: 'bg-red-500/20', text: 'text-red-400', bar: 'bg-red-500' },
    cyan: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', bar: 'bg-cyan-500' },
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      className={`relative overflow-hidden rounded-3xl bg-gradient-to-b ${gradient} border border-white/10 p-5`}
      data-testid="docs-right-panel"
      data-panel-type={type}
    >
      {/* Decorative corner accent */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-white/5 to-transparent rounded-bl-full" />
      
      {/* Header */}
      <div className="relative z-10 flex items-center gap-3 mb-5">
        <span className="text-2xl">{icon}</span>
        <h3 className="font-semibold text-white text-sm">{title}</h3>
      </div>

      {/* Content based on type */}
      <div className="relative z-10 space-y-4">
        {/* Stats type */}
        {type === 'welcome' && stats && (
          <>
            <div className="space-y-3">
              {stats.map((stat, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-xs text-white/50">{stat.label}</span>
                  <span className="text-sm font-semibold text-white">{stat.value}</span>
                </div>
              ))}
            </div>
            {tip && (
              <div className="mt-4 p-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-xs text-white/60">💡 {tip}</p>
              </div>
            )}
          </>
        )}

        {/* Steps type */}
        {type === 'steps' && steps && (
          <>
            <div className="space-y-2">
              {steps.map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold text-white/70">
                    {i + 1}
                  </div>
                  <span className="text-sm text-white/70">{step}</span>
                </div>
              ))}
            </div>
            {eta && (
              <div className="text-xs text-white/40 text-center pt-2">{eta}</div>
            )}
          </>
        )}

        {/* Diagram/Layers type */}
        {type === 'diagram' && layers && (
          <div className="space-y-2">
            {layers.map((layer, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="p-2.5 rounded-lg bg-white/5 border border-white/10 text-center"
              >
                <span className="text-xs text-white/70">{layer}</span>
              </motion.div>
            ))}
          </div>
        )}

        {/* Levels type */}
        {type === 'levels' && levels && (
          <div className="space-y-2">
            {levels.map((item, i) => {
              const colors = colorMap[item.color] || colorMap.blue;
              return (
                <div key={i} className="flex items-center gap-3" data-testid={`autonomy-level-${item.level}`}>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${colors.bg} ${colors.text}`}>
                    {item.level}
                  </span>
                  <span className="text-sm text-white/70">{item.name}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Plugins type */}
        {type === 'plugins' && plugins && (
          <div className="flex flex-wrap gap-2">
            {plugins.map((plugin, i) => (
              <span key={i} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/70">
                {plugin}
              </span>
            ))}
          </div>
        )}

        {/* Chains type */}
        {type === 'chains' && chains && (
          <div className="grid grid-cols-3 gap-2">
            {chains.map((chain, i) => (
              <div key={i} className="p-2 rounded-lg bg-white/5 border border-white/10 text-center">
                <span className="text-xs font-medium text-white/70">{chain}</span>
              </div>
            ))}
          </div>
        )}

        {/* Security features type */}
        {type === 'security' && features && (
          <div className="space-y-2">
            {features.map((feature, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-green-400 text-xs">✓</span>
                <span className="text-sm text-white/70">{feature}</span>
              </div>
            ))}
          </div>
        )}

        {/* Distribution type */}
        {type === 'distribution' && distribution && (
          <div className="space-y-2">
            {distribution.map((item, i) => {
              const colors = colorMap[item.color] || colorMap.blue;
              return (
                <div key={i} className="space-y-1" data-testid={`dist-row-${item.name}`}>
                  <div className="flex justify-between text-xs">
                    <span className="text-white/60">{item.name}</span>
                    <span className="text-white/80">{item.pct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${item.pct}%` }}
                      transition={{ duration: 0.8, delay: i * 0.1 }}
                      className={`h-full rounded-full ${colors.bar}`}
                      data-testid={`dist-bar-${item.name}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Governance types */}
        {type === 'governance' && types && (
          <div className="space-y-2">
            {types.map((t, i) => (
              <div key={i} className="p-2.5 rounded-lg bg-white/5 border border-white/10">
                <span className="text-xs font-mono text-white/70">{t}</span>
              </div>
            ))}
          </div>
        )}

        {/* Fee tiers type */}
        {type === 'fees' && tiers && (
          <div className="space-y-2">
            {tiers.map((tier, i) => (
              <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-white/5">
                <span className="text-xs text-white/60">{tier.name}</span>
                <span className="text-xs font-semibold text-green-400">{tier.discount}</span>
              </div>
            ))}
          </div>
        )}

        {/* API methods type */}
        {type === 'api' && methods && (
          <div className="space-y-2">
            {methods.map((method, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-white/40" />
                <span className="text-sm text-white/70">{method}</span>
              </div>
            ))}
          </div>
        )}

        {/* Events type */}
        {type === 'events' && events && (
          <div className="space-y-2">
            {events.map((event, i) => (
              <div key={i} className="p-2 rounded-lg bg-white/5 border border-white/10">
                <code className="text-xs text-white/70 font-mono">{event}</code>
              </div>
            ))}
          </div>
        )}

        {/* Default type */}
        {type === 'default' && (
          <div className="text-center py-4">
            <p className="text-xs text-white/40">Explore this section</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// Animated Navigation Card
function DocNavCard({ direction, title }) {
  const isNext = direction === 'next';
  
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={`relative overflow-hidden rounded-xl p-5 bg-white/[0.03] border border-white/10 hover:border-white/20 transition-all duration-300 ${
        isNext ? 'text-right' : 'text-left'
      }`}
    >
      {/* Animated gradient on hover */}
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
        isNext 
          ? 'bg-gradient-to-l from-white/5 to-transparent' 
          : 'bg-gradient-to-r from-white/5 to-transparent'
      }`} />
      
      <div className="relative z-10">
        <div className={`flex items-center gap-2 text-xs text-white/40 mb-2 ${isNext ? 'justify-end' : ''}`}>
          {!isNext && <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" />}
          <span>{isNext ? 'Next' : 'Previous'}</span>
          {isNext && <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />}
        </div>
        <p className="font-medium text-white group-hover:text-white transition-colors">{title}</p>
      </div>
    </motion.div>
  );
}

// Animated Feature Card for docs content
export function FeatureCard({ icon, title, description, href }) {
  const content = (
    <motion.div
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      className="relative group overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-white/[0.08] to-white/[0.02] border border-white/10 hover:border-white/20 transition-all duration-300"
    >
      {/* Animated shine effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
      </div>
      
      {/* Glow effect */}
      <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-white/0 via-white/10 to-white/0 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
      
      <div className="relative z-10">
        {icon && (
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4 group-hover:bg-white/15 transition-colors">
            {icon}
          </div>
        )}
        <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-white transition-colors">{title}</h3>
        <p className="text-sm text-white/50 leading-relaxed">{description}</p>
        {href && (
          <div className="mt-4 flex items-center gap-1 text-sm text-white/40 group-hover:text-white/60 transition-colors">
            <span>Learn more</span>
            <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </div>
        )}
      </div>
    </motion.div>
  );

  if (href) {
    return <Link to={href}>{content}</Link>;
  }
  return content;
}

// Code Block Component
export function CodeBlock({ children, language = 'javascript' }) {
  return (
    <div className="relative group rounded-xl overflow-hidden bg-[#0d0d0d] border border-white/10">
      <div className="flex items-center justify-between px-4 py-2 bg-white/5 border-b border-white/10">
        <span className="text-xs text-white/40 font-mono">{language}</span>
      </div>
      <pre className="p-4 overflow-x-auto">
        <code className="text-sm font-mono text-white/80">{children}</code>
      </pre>
    </div>
  );
}

// Info Callout Component with ? icon
export function Callout({ type = 'info', title, children }) {
  const styles = {
    info: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', Icon: HelpCircle },
    warning: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', Icon: AlertTriangle },
    success: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400', Icon: CheckCircle },
    error: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', Icon: XCircle },
  };

  const { bg, border, text, Icon } = styles[type] || styles.info;

  return (
    <div className={`rounded-xl p-4 border ${bg} ${border} my-6 flex gap-3`}>
      <div className={`flex-shrink-0 mt-0.5 ${text}`}>
        <Icon size={18} />
      </div>
      <div className="flex-1">
        {title && <p className={`font-semibold mb-1 ${text}`}>{title}</p>}
        <div className="text-white/70 text-sm leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

// Custom Animated Content Card
export function ContentCard({ icon, title, description, children, className = '' }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={`relative group overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 hover:border-white/20 transition-all duration-300 ${className}`}
    >
      {/* Animated corner accent */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      
      {/* Animated bottom line */}
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-white/20 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
      
      <div className="relative z-10">
        {icon && (
          <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
            {icon}
          </div>
        )}
        {title && <h3 className="text-base font-semibold text-white mb-2">{title}</h3>}
        {description && <p className="text-sm text-white/50 leading-relaxed">{description}</p>}
        {children}
      </div>
    </motion.div>
  );
}

// Step Card for numbered steps
export function StepCard({ number, title, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: number * 0.1 }}
      className="flex gap-4 group"
    >
      <div className="flex-shrink-0">
        <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white font-bold group-hover:bg-white/15 group-hover:scale-110 transition-all duration-300">
          {number}
        </div>
      </div>
      <div className="flex-1 pt-1">
        <h4 className="text-white font-medium mb-2">{title}</h4>
        <div className="text-white/60 text-sm leading-relaxed">{children}</div>
      </div>
    </motion.div>
  );
}
